import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { OrganizationSuggestionsResponse } from 'store/signup/types';
import { ModalContent, ModalFooter, ModalHeader } from './style';
import { GivelifyLabel, GivelifyLink } from '@givelify/ui';
import { SuggestionItem } from './SuggestionItem';
import { pushEvent } from 'utils/clevertapUtils';
import {
    CLICK_EVENT,
    RESULTS_MODAL,
    VIEW_EVENT,
} from 'utils/clevertapEvents';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'routes';
import { useSelector } from 'react-redux';
import { AppState } from 'store';
import { Features } from 'store/featureFlags/types';
import { useScrollPosition } from 'pages/signup/utils/useScrollPosition';
import { SEARCH_EXPERIMENT, SEARCH_UI_EXPERIMENT } from 'utils/clevertapVariantFlags';

type ResultsListProps = {
    list: OrganizationSuggestionsResponse[];
    onSelect: (data: OrganizationSuggestionsResponse) => void;
};

export const ResultsList: React.FC<ResultsListProps> = ({ list, onSelect }) => {
    const { t } = useTranslation();
    const push = useNavigate();
    const contentRef = useRef<HTMLDivElement>(null);
    const { newSearchUIEnabled, v2SearchAPIEnabled } = useSelector(
        (state: AppState) => ({
            newSearchUIEnabled:
                state.FeatureFlagging.features[Features.SEARCH_UI_V2],
            v2SearchAPIEnabled:
                state.FeatureFlagging.features[Features.SEARCH_API_V2],
        }),
    );

    const copy = useMemo(
        () => ({
            head1: t('signup.organizationSearch.suggestionsList.head1'),
            head2: t('signup.organizationSearch.suggestionsList.head2'),
            notHere: t('signup.organizationSearch.suggestionsList.notHere'),
            enterDetails: t(
                'signup.organizationSearch.suggestionsList.enterDetails',
            ),
        }),
        [t],
    );

    const manualEntry = () => {
        pushEvent(CLICK_EVENT, {
            name: "Enter",
            location: RESULTS_MODAL,
            [SEARCH_EXPERIMENT]: v2SearchAPIEnabled ? 'on' : 'off',
            [SEARCH_UI_EXPERIMENT]: newSearchUIEnabled ? 'on' : 'off',
        });
        push(PATH.SIGNUP.STEPS.ADD_ORGANIZATION);
    };

    useEffect(() => {
        pushEvent(VIEW_EVENT, {
            name: RESULTS_MODAL,
            [SEARCH_EXPERIMENT]: v2SearchAPIEnabled ? 'on' : 'off',
            [SEARCH_UI_EXPERIMENT]: newSearchUIEnabled ? 'on' : 'off',
        });
    }, [newSearchUIEnabled, v2SearchAPIEnabled]);

    useScrollPosition(contentRef);

    return (
        <>
            <ModalHeader className="fs-unmask">
                <GivelifyLabel text={copy.head1} variant="body2" />
                <GivelifyLabel text={copy.head2} variant="heading3S" />
            </ModalHeader>
            <ModalContent className="fs-unmask" ref={contentRef}>
                {list.map((item, index) => {
                    return (
                        <SuggestionItem
                            onSelect={onSelect}
                            item={item}
                            key={item.id}
                        />
                    );
                })}
            </ModalContent>
            <ModalFooter className="fs-unmask">
                <GivelifyLabel text={copy.notHere} variant="body1B" />
                <GivelifyLink
                    className="link"
                    text={copy.enterDetails}
                    onClick={manualEntry}
                    id="enter-manually"
                />
            </ModalFooter>
        </>
    );
};
